import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { ReviewStrip } from "@/components/review-strip";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { FEES, FIRM, PENINSULA_ADDRESS, faqSchema } from "@/lib/firm";

const TITLE = "Conveyancing Services QLD | Fixed Fee Solicitors";
const DESCRIPTION = `Solicitor-led conveyancing services across Queensland. Sale ${FEES.sale} and purchase ${FEES.purchase} incl. GST, quoted up front. Call ${FIRM.phone}.`;

const FAQS = [
  {
    q: "What is included in your conveyancing services?",
    a: `Contract review before you sign, all searches appropriate to the property, managing the finance and building and pest conditions, seller disclosure (Form 2) compliance, transfer duty lodgement, adjustments for rates and water, electronic settlement through PEXA, and confirmation to you and your agent on the day. Our fixed fee is ${FEES.sale} ${FEES.saleNote} for a sale and ${FEES.purchase} ${FEES.purchaseNote} for a purchase, plus disbursements at cost.`,
  },
  {
    q: "Should I use a solicitor or a conveyancer in Queensland?",
    a: "Queensland does not licence non-solicitor conveyancers the way New South Wales and Victoria do — property transfers here are legal work carried out by or under a law practice. Practically, the difference is what happens when the matter stops being a straight transfer: a contract dispute, a deceased estate in the title, a family transfer, a caveat, a granny flat arrangement or a business sold with the freehold. A solicitor can act on all of it; a conveyancing clerk has to refer it out.",
  },
  {
    q: "How much do conveyancing services cost?",
    a: `Our professional fees are fixed: ${FEES.sale} ${FEES.saleNote} to sell and ${FEES.purchase} ${FEES.purchaseNote} to buy. Disbursements — title and council searches, PEXA fees, registration fees and transfer duty — are separate and charged at cost. There is a full breakdown, including current transfer duty rates, on our fees page.`,
  },
  {
    q: "When should I engage a conveyancing solicitor?",
    a: "Before you sign, not after. Once a contract is signed the terms are set, and the five-business-day cooling-off period on a residential purchase is short and comes with a 0.25% penalty. Sellers should engage even earlier — since 1 August 2025 a compliant disclosure statement and prescribed certificates must be given to the buyer before they sign.",
  },
  {
    q: "How long does conveyancing take in Queensland?",
    a: "Standard REIQ contracts usually run 30 days to settlement, with finance around day 14 to 21 and building and pest around day 14. Off-the-plan, deceased estate, body corporate and subdivision matters can take longer. We work to the dates in your contract and tell you early if one is at risk.",
  },
  {
    q: "Do you act for buyers and sellers anywhere in Queensland?",
    a: `Yes. Settlements are electronic, so the office does not need to be near the property. We act across the Sunshine Coast, Moreton Bay and the Peninsula, the Fraser Coast and Wide Bay, and Brisbane. We have offices at ${FIRM.street}, ${FIRM.suburb} and ${PENINSULA_ADDRESS}.`,
  },
  {
    q: "Can you handle more than a standard house sale?",
    a: "Yes — units and townhouses with body corporate records to review, off-the-plan contracts, vacant land and house-and-land, related-party and family transfers, transmission applications on deceased estates, commercial and industrial property, business sales including the freehold, and rural and acreage transactions.",
  },
];

const CONTRACT_GUIDES: { slug: string; label: string }[] = [
  {
    slug: "can-a-buyer-or-seller-pull-out-of-an-unconditional-contract-queensland",
    label: "Can a buyer or seller pull out of an unconditional contract?",
  },
  { slug: "vacant-possession-queensland", label: "What vacant possession means at settlement" },
  {
    slug: "adding-or-removing-a-name-from-a-property-title-queensland",
    label: "Adding or removing a name from a property title",
  },
  {
    slug: "withdrawing-an-offer-on-a-house-queensland",
    label: "Withdrawing an offer on a house",
  },
  { slug: "sunset-clauses-queensland", label: "Sunset clauses: off the plan and subject to sale" },
  {
    slug: "simultaneous-settlement-queensland",
    label: "Simultaneous settlement: selling and buying the same day",
  },
  { slug: "title-search-queensland", label: "Title search QLD: how to get a copy of your title" },
  { slug: "stamp-duty-queensland", label: "Stamp duty QLD: rates, concessions and when it is paid" },
];

export const Route = createFileRoute("/conveyancing-services")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary" },
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://coastsidelaw.com.au/conveyancing-services" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/conveyancing-services" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LegalService",
          name: `${FIRM.name} — Conveyancing Services`,
          description: DESCRIPTION,
          telephone: FIRM.phone,
          areaServed: [
            { "@type": "State", name: "Queensland" },
            { "@type": "Place", name: "Sunshine Coast" },
            { "@type": "Place", name: "Moreton Bay" },
            { "@type": "Place", name: "Wide Bay" },
            { "@type": "Place", name: "Brisbane" },
          ],
          address: {
            "@type": "PostalAddress",
            streetAddress: FIRM.street,
            addressLocality: FIRM.suburb,
            addressRegion: FIRM.state,
            postalCode: FIRM.postcode,
            addressCountry: FIRM.country,
          },
          serviceType: [
            "Residential conveyancing",
            "Property sale conveyancing",
            "Property purchase conveyancing",
            "Off the plan conveyancing",
            "Commercial conveyancing",
            "Transfer of property between family members",
          ],
        }),
      },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
    ],
  }),
  component: ConveyancingServicesPage,
});

const SERVICES = [
  {
    title: "Selling a property",
    body: "Disclosure statement and prescribed certificates prepared before the property goes under contract, contract review, deposit and trust account handling, adjustments and payout of your mortgage at settlement.",
  },
  {
    title: "Buying a property",
    body: "Contract review before you sign, cooling-off advice, searches, finance and building and pest conditions managed to their dates, transfer duty assessed and lodged, and electronic settlement.",
  },
  {
    title: "Units, townhouses and body corporate",
    body: "Body corporate records searched and reported on — levies, sinking fund, by-laws, insurance, defects and any litigation on foot — before your cooling-off period runs out.",
  },
  {
    title: "Off the plan and new builds",
    body: "Sunset clauses, variation rights, disclosure statements and the practical timing risk of buying something that does not exist yet, explained before you commit.",
  },
  {
    title: "Family and related-party transfers",
    body: "Transfers between spouses, to children, into or out of trusts and self-managed super funds, with the transfer duty and exemption position confirmed first.",
  },
  {
    title: "Deceased estates in the title",
    body: "Transmission applications, selling as executor and dealing with a title where the registered owner has died — handled in-house alongside the estate administration.",
  },
  {
    title: "Commercial and industrial property",
    body: "Offices, retail, sheds and tenanted investments, where GST treatment, leases and permitted use decide the outcome.",
  },
  {
    title: "Rural, acreage and business sales",
    body: "Water and bore rights, vegetation and biosecurity, plant and licences, and freehold sold together with a going business.",
  },
];

const INCLUSIONS = [
  "Contract review and advice before you sign",
  "All standard searches for the property type",
  "Seller disclosure (Form 2) preparation or review",
  "Managing finance, building and pest and other conditions",
  "Liaison with your agent, broker and bank",
  "Transfer duty assessment and lodgement",
  "Rates, water and body corporate adjustments",
  "Electronic settlement through PEXA",
  "Same-day confirmation once settlement is effected",
];

function ConveyancingServicesPage() {
  return (
    <>
      <PageHero
        eyebrow="Conveyancing services"
        title="Conveyancing services across Queensland"
        lead={`Fixed fee, solicitor-led, and the same lawyer from contract review to settlement. ${FEES.sale} to sell, ${FEES.purchase} to buy, both including GST.`}
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="space-y-5 text-base leading-relaxed text-muted-foreground">
          <p>
            Conveyancing is the legal transfer of property from one owner to another — but the value
            of the service is almost entirely in what happens before settlement day. Reading the
            contract before it is signed, getting the seller disclosure right, finding what the
            searches turn up, and holding the other side to the dates. Settlement itself is the easy
            part.
          </p>
          <p>
            We act for buyers and sellers across the Sunshine Coast, Moreton Bay and the Redcliffe
            Peninsula, the Fraser Coast and Brisbane. Settlements are electronic, so where you are
            does not limit who can act for you.
          </p>
        </div>

        <div className="mt-14 rounded-sm border border-border bg-card p-7">
          <h2 className="font-serif text-2xl text-foreground">Our fixed fees</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="rounded-sm border border-border p-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-accent-foreground">
                Selling
              </p>
              <p className="mt-2 font-serif text-3xl text-foreground">{FEES.sale}</p>
              <p className="text-sm text-muted-foreground">{FEES.saleNote}</p>
            </div>
            <div className="rounded-sm border border-border p-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-accent-foreground">
                Buying
              </p>
              <p className="mt-2 font-serif text-3xl text-foreground">{FEES.purchase}</p>
              <p className="text-sm text-muted-foreground">{FEES.purchaseNote}</p>
            </div>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Disbursements — searches, registration fees, PEXA and transfer duty — are additional and
            charged at cost.{" "}
            <Link to="/fees" className="font-semibold text-foreground underline underline-offset-4">
              See the full cost breakdown and duty rates
            </Link>
            .
          </p>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">What we act on</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {SERVICES.map((s) => (
              <div key={s.title} className="rounded-sm border border-border bg-card p-5">
                <h3 className="font-serif text-xl text-foreground">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">What the fixed fee covers</h2>
          <ul className="mt-6 grid gap-2 sm:grid-cols-2">
            {INCLUSIONS.map((t) => (
              <li
                key={t}
                className="rounded-sm border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
              >
                {t}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-14 rounded-sm border-l-2 border-accent bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">
            Solicitor-led, and why that matters
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Most conveyancing matters run smoothly and any competent office can settle them. The
            difference shows up in the ones that do not: a caveat lodged over the title, a seller who
            has died mid-contract, a disclosure statement that turns out to be defective, a buyer who
            will not settle. Those become legal problems, and a firm that cannot act on them has to
            hand you to someone else at the worst possible moment. We do that work in-house.
          </p>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Related pages</h2>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/conveyancing"
              className="rounded-sm border border-border px-5 py-2.5 text-sm font-semibold text-foreground card-elevate"
            >
              Residential conveyancing
            </Link>
            <Link
              to="/commercial-conveyancing"
              className="rounded-sm border border-border px-5 py-2.5 text-sm font-semibold text-foreground card-elevate"
            >
              Commercial conveyancing
            </Link>
            <Link
              to="/seller-disclosure"
              className="rounded-sm border border-border px-5 py-2.5 text-sm font-semibold text-foreground card-elevate"
            >
              Seller disclosure (Form 2)
            </Link>
            <Link
              to="/fees"
              className="rounded-sm border border-border px-5 py-2.5 text-sm font-semibold text-foreground card-elevate"
            >
              Conveyancing costs QLD
            </Link>
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Contract questions answered</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            The questions we are asked most often about Queensland contracts, answered by a
            solicitor rather than left at "seek legal advice".
          </p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {CONTRACT_GUIDES.map((g) => (
              <li key={g.slug}>
                <Link
                  to="/guides/$slug"
                  params={{ slug: g.slug }}
                  className="block rounded-sm border border-border bg-card p-4 text-sm font-semibold text-foreground card-elevate"
                >
                  {g.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} />


        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Conveyancing services FAQs</h2>
          <div className="mt-6">
            <FaqList items={FAQS} />
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Get a fixed-fee quote</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Send us the property and where the deal is up to. If you have a contract, send that too —
            we will read it before you sign. Offices at {FIRM.street}, {FIRM.suburb} and{" "}
            {PENINSULA_ADDRESS}.
          </p>
          <div className="mt-6">
            <EnquiryForm />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5">
        <ReviewStrip />
      </section>

      <CtaBand title="Buying or selling in Queensland?" />
      <div className="h-20" />
    </>
  );
}
